import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * NotificationsProvider — single source of truth for the notification bell.
 *
 * Motivation:
 *  Two <NotificationBell/> instances render simultaneously (desktop + mobile
 *  top-bar). Before this context, each ran its own 20s poll, hitting
 *  /api/notifications twice per interval per logged-in admin. This provider
 *  hoists the fetch, so we do it once at the app level and both bells (plus
 *  any future consumer — e.g. an inbox badge in the sidebar) share state.
 *
 *  It ALSO drives optional native browser notifications: admins can flip a
 *  toggle in the bell dropdown, we call Notification.requestPermission(),
 *  and from then on new items produce an OS-level notification whenever the
 *  tab is hidden. Clicking the OS notification focuses the tab and navigates
 *  to the item's link.
 */

const NotificationsContext = createContext(null);
const POLL_MS = 20_000;
const NATIVE_PREF_KEY = "rallylab.notifications.native";
const SOUND_MODE_KEY = "rallylab.notifications.sound_mode";  // iter146 — "silent" | "chirp" | "whistle"
const LEGACY_SOUND_KEY = "rallylab.notifications.sound";     // iter143 boolean, migrated on read
const QUIET_START_KEY = "rallylab.notifications.quiet_start"; // iter147 — "HH:MM" or ""
const QUIET_END_KEY = "rallylab.notifications.quiet_end";

const readNativePref = () => {
  try {
    return localStorage.getItem(NATIVE_PREF_KEY) === "1";
  } catch { return false; }
};
const writeNativePref = (on) => {
  try { localStorage.setItem(NATIVE_PREF_KEY, on ? "1" : "0"); } catch { /* noop */ }
};

// Iter146 — Three-way sound preference: silent, chirp (default, iter143's
// original two-note pling), or whistle (referee-style short double blast).
// We migrate the older boolean key on read so returning users don't lose
// their setting: `sound=0` maps to silent, anything else to chirp.
const SOUND_MODES = ["silent", "chirp", "whistle"];
const readSoundMode = () => {
  try {
    const v = localStorage.getItem(SOUND_MODE_KEY);
    if (v && SOUND_MODES.includes(v)) return v;
    const legacy = localStorage.getItem(LEGACY_SOUND_KEY);
    if (legacy === "0") return "silent";
    return "chirp";
  } catch { return "chirp"; }
};
const writeSoundMode = (mode) => {
  try { localStorage.setItem(SOUND_MODE_KEY, SOUND_MODES.includes(mode) ? mode : "chirp"); } catch { /* noop */ }
};

// Iter147 — Quiet hours. Users pick a start & end (HH:MM). If the current
// local time falls inside the window, the sound is skipped (bell visuals
// still fire). Handles overnight windows (e.g. 22:00 → 07:00) by checking
// whether the window wraps midnight.
const _readQuiet = () => {
  try {
    return {
      start: localStorage.getItem(QUIET_START_KEY) || "",
      end: localStorage.getItem(QUIET_END_KEY) || "",
    };
  } catch { return { start: "", end: "" }; }
};
const _writeQuiet = (start, end) => {
  try {
    localStorage.setItem(QUIET_START_KEY, start || "");
    localStorage.setItem(QUIET_END_KEY, end || "");
  } catch { /* noop */ }
};
const _isInQuietHours = () => {
  const { start, end } = _readQuiet();
  if (!start || !end) return false;
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return false;
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  if (startMin === endMin) return false; // zero-length window
  if (startMin < endMin) return nowMin >= startMin && nowMin < endMin;
  // Overnight window — active if we're past start OR before end.
  return nowMin >= startMin || nowMin < endMin;
};

let _sharedAudioCtx = null;
const _getAudioCtx = () => {
  if (typeof window === "undefined") return null;
  if (_sharedAudioCtx) return _sharedAudioCtx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _sharedAudioCtx = new AC();
    return _sharedAudioCtx;
  } catch { return null; }
};

const _playChirp = (ctx, now) => {
  // Two-note pling: E6 → G6. ~420 ms total, sine wave, soft envelope.
  const master = ctx.createGain();
  master.gain.value = 0.001;
  master.connect(ctx.destination);
  master.gain.setValueAtTime(0.001, now);
  master.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
  master.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
  [{ f: 1318.5, s: 0, d: 0.18 }, { f: 1568.0, s: 0.14, d: 0.24 }].forEach(({ f, s, d }) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f, now + s);
    osc.connect(master);
    osc.start(now + s);
    osc.stop(now + s + d);
  });
};

const _playWhistle = (ctx, now) => {
  // Referee whistle: two short blasts (~180 ms each, 120 ms gap). Fundamental
  // ~2600 Hz with a LFO shudder emulating the pea-rattle. Square wave with a
  // low-pass filter softens the harshness so it reads as "whistle" not "beep".
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 5200;
  filter.Q.value = 0.9;
  const master = ctx.createGain();
  master.gain.value = 0.001;
  filter.connect(master);
  master.connect(ctx.destination);
  const blast = (start, dur) => {
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(2650, now + start);
    // Faint pitch drift so it sounds organic rather than a synth beep.
    osc.frequency.linearRampToValueAtTime(2720, now + start + dur * 0.4);
    osc.frequency.linearRampToValueAtTime(2600, now + start + dur);
    // Pea-rattle LFO: quick amplitude tremolo at ~28 Hz.
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 28;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 60;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    osc.connect(filter);
    // Envelope: sharp attack, fast decay.
    master.gain.setValueAtTime(0.001, now + start);
    master.gain.exponentialRampToValueAtTime(0.28, now + start + 0.015);
    master.gain.setValueAtTime(0.28, now + start + dur - 0.05);
    master.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
    osc.start(now + start);
    lfo.start(now + start);
    osc.stop(now + start + dur);
    lfo.stop(now + start + dur);
  };
  blast(0, 0.18);
  blast(0.30, 0.22);
};

const _playSound = (mode) => {
  if (!mode || mode === "silent") return;
  if (_isInQuietHours()) return;  // iter147 — respect the mute window
  const ctx = _getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    if (mode === "whistle") _playWhistle(ctx, now);
    else _playChirp(ctx, now);
  } catch { /* silent — browser blocked audio */ }
};

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [nativeEnabled, setNativeEnabled] = useState(false);
  // Iter146 — Persisted sound mode; auto-migrates the legacy boolean key.
  const [soundMode, setSoundModeState] = useState(() => readSoundMode());
  // Iter147 — Quiet hours window ("HH:MM" strings). Empty strings = disabled.
  const [quietHours, setQuietHoursState] = useState(() => _readQuiet());

  // Iter148 — Hydrate sound + quiet-hours from the user doc when we know who
  // is signed in. Server values win over localStorage so the preference
  // follows the account across devices. Fire-and-forget: if the user has no
  // saved prefs yet we keep the local cache.
  useEffect(() => {
    if (!user) return;
    const serverMode = user.notification_sound_mode;
    const serverStart = user.notification_quiet_start;
    const serverEnd = user.notification_quiet_end;
    if (serverMode && SOUND_MODES.includes(serverMode) && serverMode !== soundMode) {
      writeSoundMode(serverMode);
      setSoundModeState(serverMode);
    }
    if (typeof serverStart === "string" || typeof serverEnd === "string") {
      const nextStart = typeof serverStart === "string" ? serverStart : quietHours.start;
      const nextEnd = typeof serverEnd === "string" ? serverEnd : quietHours.end;
      if (nextStart !== quietHours.start || nextEnd !== quietHours.end) {
        _writeQuiet(nextStart, nextEnd);
        setQuietHoursState({ start: nextStart, end: nextEnd });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const _syncPrefs = useCallback((patch) => {
    // Best-effort — offline / 401 stays silent so we don't spam the toast.
    api.put("/me/notification-preferences", patch).catch(() => {});
  }, []);
  const lastUnreadRef = useRef(null);        // null until first successful load
  const seenIdsRef = useRef(new Set());
  const timerRef = useRef(null);

  const nativeSupported = typeof window !== "undefined" && "Notification" in window;

  // Sync stored pref + browser permission on mount / user change.
  useEffect(() => {
    if (!user) { setNativeEnabled(false); return; }
    if (!nativeSupported) { setNativeEnabled(false); return; }
    const wants = readNativePref();
    setNativeEnabled(wants && Notification.permission === "granted");
  }, [user, nativeSupported]);

  const fireNativeNotif = useCallback((n) => {
    if (!nativeSupported) return;
    if (Notification.permission !== "granted") return;
    // Only surface OS-level noise when the tab is out of focus — otherwise the
    // in-app Sonner toast is enough and firing both would be double-dipping.
    if (document.visibilityState === "visible") return;
    try {
      const notif = new Notification(n.title || "Rally Lab", {
        body: n.body || undefined,
        icon: "/logo192.png",
        tag: `rally-${n.id}`,   // replaces prior notif with same tag so we don't stack
        renotify: false,
      });
      notif.onclick = (ev) => {
        ev.preventDefault();
        window.focus();
        if (n.link) navigate(n.link);
        notif.close();
      };
    } catch { /* noop — some browsers throw on tag collisions */ }
  }, [nativeSupported, navigate]);

  const load = useCallback(async () => {
    if (!user) {
      // Reset on logout so the next login starts clean.
      setItems([]); setUnread(0);
      lastUnreadRef.current = null;
      seenIdsRef.current = new Set();
      return;
    }
    try {
      const { data } = await api.get("/notifications?limit=8");
      const nextItems = data.items || [];
      const nextUnread = data.unread_count || 0;

      // Silent seed on the first load — otherwise a page refresh spams N toasts
      // for every currently-unread item. From the second load onward, any
      // NEW-id-AND-still-unread item triggers a toast + pulse + optional native.
      if (lastUnreadRef.current !== null) {
        const fresh = nextItems.filter((n) => !seenIdsRef.current.has(n.id) && !n.is_read);
        if (fresh.length > 0) {
          setPulse(true);
          setTimeout(() => setPulse(false), 4000);
          _playSound(readSoundMode());  // iter146 — user-picked mode (silent/chirp/whistle)
          fresh.slice(0, 3).forEach((n) => {
            toast(n.title || "New notification", {
              description: n.body || undefined,
              action: n.link ? { label: "Review", onClick: () => navigate(n.link) } : undefined,
              duration: 8000,
            });
            fireNativeNotif(n);
          });
        }
      }
      nextItems.forEach((n) => seenIdsRef.current.add(n.id));
      setItems(nextItems);
      setUnread(nextUnread);
      lastUnreadRef.current = nextUnread;
    } catch {
      // Silent — the bell shouldn't nag on failure.
    }
  }, [user, navigate, fireNativeNotif]);

  // Initial load whenever the logged-in user changes.
  useEffect(() => { load(); }, [load]);

  // Fast poll while the tab is visible; pause when hidden to save battery on
  // mobile PWAs. On resume we do an immediate refresh so admins never wait
  // 20s to see a badge move after Alt-Tabbing back.
  useEffect(() => {
    if (!user) return undefined;
    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(load, POLL_MS);
    };
    const stop = () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") { load(); start(); }
      else { stop(); }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [user, load]);

  const markRead = useCallback(async (n) => {
    if (n.is_read) return;
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    load();
  }, [load]);

  const markAllRead = useCallback(async () => {
    try { await api.post("/notifications/mark-all-read"); } catch { /* noop */ }
    load();
  }, [load]);

  const openNotif = useCallback(async (n) => {
    if (!n.is_read) await markRead(n);
    if (n.link) navigate(n.link);
  }, [navigate, markRead]);

  const requestNative = useCallback(async () => {
    if (!nativeSupported) {
      toast.error("This browser doesn't support desktop notifications.");
      return false;
    }
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission === "granted") {
      writeNativePref(true);
      setNativeEnabled(true);
      toast.success("Desktop notifications on. You'll get alerts even when the tab is in the background.");
      return true;
    }
    writeNativePref(false);
    setNativeEnabled(false);
    if (permission === "denied") {
      toast.error("Blocked by the browser. Enable notifications for this site in your browser settings, then try again.");
    }
    return false;
  }, [nativeSupported]);

  const disableNative = useCallback(() => {
    writeNativePref(false);
    setNativeEnabled(false);
    toast("Desktop notifications off.", { description: "You'll still see the in-app bell." });
  }, []);

  const value = {
    items, unread, pulse,
    load, markRead, markAllRead, openNotif,
    // Native browser notifications:
    nativeSupported,
    nativeEnabled,
    nativePermission: nativeSupported ? Notification.permission : "unsupported",
    requestNative,
    disableNative,
    // Iter146 — Sound mode: "silent" | "chirp" | "whistle" + preview helper.
    soundMode,
    setSoundMode: (mode) => {
      if (!SOUND_MODES.includes(mode)) return;
      writeSoundMode(mode);
      setSoundModeState(mode);
      _playSound(mode);  // instant preview so the user hears their pick
      _syncPrefs({ sound_mode: mode });  // iter148 — persist to user doc
    },
    previewSound: (mode) => _playSound(mode || soundMode),
    // Iter147 — Quiet hours read/write. Setting either to "" disables the
    // window. Preview intentionally does NOT respect quiet hours so the
    // user can still hear their pick while configuring.
    quietHours,
    setQuietHours: (start, end) => {
      const cleanStart = start || "";
      const cleanEnd = end || "";
      _writeQuiet(cleanStart, cleanEnd);
      setQuietHoursState({ start: cleanStart, end: cleanEnd });
      _syncPrefs({ quiet_start: cleanStart, quiet_end: cleanEnd });  // iter148
    },
    isQuietNow: () => _isInQuietHours(),
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationsProvider>");
  return ctx;
};
